import { useState, useRef, useEffect } from 'react'
import { IconCheck, IconClose, IconPlus } from './components/Icons'
import { hexToHsl, hslToHex } from './utils/colorStyles'
import { getColorName } from './utils/colorNames'
import './ColorPalette.css'

export function ColorPalette({
  colors,
  currentColor,
  onSelect,
  onAddColor,
  onRemoveColor,
  onMixColors,
  sensitivePress,
  lang = 'he',
}) {
  const [showMixer, setShowMixer] = useState(false)
  const [showSpectrum, setShowSpectrum] = useState(false)
  const [mixColor1, setMixColor1] = useState('#ff0000')
  const [mixColor2, setMixColor2] = useState('#0000ff')
  const [mixRatio, setMixRatio] = useState(0.5)
  const [hue, setHue] = useState(0)
  const [sat, setSat] = useState(100)
  const [light, setLight] = useState(50)
  const slCanvasRef = useRef(null)
  const SL_W = 200
  const SL_H = 140

  useEffect(() => {
    if (showSpectrum && currentColor) {
      const [h, s, l] = hexToHsl(currentColor)
      setHue(h)
      setSat(s)
      setLight(l)
    }
  }, [showSpectrum, currentColor])

  useEffect(() => {
    const canvas = slCanvasRef.current
    if (!canvas || !showSpectrum) return
    const ctx = canvas.getContext('2d')
    for (let py = 0; py < SL_H; py++) {
      for (let px = 0; px < SL_W; px++) {
        const s = (px / SL_W) * 100
        const l = (1 - py / SL_H) * 100
        ctx.fillStyle = `hsl(${hue}, ${s}%, ${l}%)`
        ctx.fillRect(px, py, 1, 1)
      }
    }
  }, [showSpectrum, hue])

  const spectrumHex = hslToHex(hue, sat, light)

  const handleSlBoxClick = (e) => {
    const el = e.currentTarget
    const rect = el.getBoundingClientRect()
    const x = (e.clientX - rect.left) / rect.width
    const y = (e.clientY - rect.top) / rect.height
    const s = Math.max(0, Math.min(100, x * 100))
    const l = Math.max(0, Math.min(100, (1 - y) * 100))
    setSat(s)
    setLight(l)
    onSelect(hslToHex(hue, s, l))
  }

  const mixed = onMixColors(mixColor1, mixColor2, mixRatio)

  const handleAddMixed = () => {
    onAddColor(mixed)
    onSelect(mixed)
    setShowMixer(false)
  }

  const handleSelect = (hex) => onSelect(hex)
  return (
    <div className={`color-palette ${sensitivePress ? 'sensitive-press' : ''}`}>
      <div className="swatches">
        {colors.map((hex) => (
          (() => {
            const name = getColorName(hex, lang)
            return (
          <div
            key={hex}
            role="button"
            tabIndex={0}
            className={`swatch ${hex === currentColor ? 'selected' : ''}`}
            style={{ background: hex }}
            onClick={() => handleSelect(hex)}
            onPointerDown={sensitivePress ? (e) => { e.preventDefault(); handleSelect(hex); } : undefined}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), handleSelect(hex))}
            title={name}
            aria-label={name}
            data-speak={name}
          >
            {hex === currentColor && <span className="check"><IconCheck size={16} /></span>}
            <button
              type="button"
              className="remove-color"
              onClick={(e) => {
                e.stopPropagation()
                onRemoveColor(hex)
              }}
              onPointerDown={(e) => e.stopPropagation()}
              title={lang === 'en' ? 'Remove color' : 'הסר צבע'}
            >
              <IconClose size={14} />
            </button>
          </div>
            )
          })()
        ))}
        <button
          type="button"
          className="swatch add-swatch"
          onClick={() => setShowMixer(!showMixer)}
          title={lang === 'en' ? 'Mix / add color' : 'ערבב צבעים / הוסף צבע'}
        >
          <IconPlus size={18} />
        </button>
      </div>

      <div className="spectrum-toggle-row">
        <button
          type="button"
          className="spectrum-toggle-btn"
          onClick={() => setShowSpectrum(!showSpectrum)}
          title={lang === 'en' ? 'Pick any color from the spectrum' : 'בחירת צבע מכל הספקטרום - חיבור בין כל הצבעים'}
        >
          {showSpectrum ? (lang === 'en' ? '▼ Close spectrum' : '▼ סגור ספקטרום') : (lang === 'en' ? '🎨 Spectrum color' : '🎨 צבע מכל הספקטרום')}
        </button>
      </div>

      {showSpectrum && (
        <div className="spectrum-picker">
          <div className="spectrum-hue-strip-wrap">
            <span className="spectrum-label">{lang === 'en' ? 'Hue' : 'גוון'}</span>
            <input
              type="range"
              min="0"
              max="360"
              value={hue}
              onChange={(e) => setHue(Number(e.target.value))}
              className="spectrum-hue-slider"
            />
            <div
              className="spectrum-hue-bar"
              style={{ background: 'linear-gradient(to left, #f00, #ff0, #0f0, #0ff, #00f, #f0f, #f00)' }}
            />
          </div>
          <div className="spectrum-sl-wrap">
            <span className="spectrum-label">{lang === 'en' ? 'Saturation + lightness (click to pick)' : 'רוויה + בהירות (לחיצה לבחירה)'}</span>
            <div className="spectrum-sl-box-wrap" onClick={handleSlBoxClick}>
              <canvas ref={slCanvasRef} width={SL_W} height={SL_H} className="spectrum-sl-canvas" />
              <span
                className="spectrum-sl-cursor"
                style={{ left: `${(sat / 100) * 100}%`, top: `${(1 - light / 100) * 100}%`, background: spectrumHex }}
              />
            </div>
          </div>
          <div className="spectrum-result">
            <div className="spectrum-preview" style={{ background: spectrumHex }} />
            <span className="spectrum-hex">{spectrumHex}</span>
            <button type="button" className="spectrum-use" onClick={() => onSelect(spectrumHex)}>{lang === 'en' ? 'Use' : 'השתמש'}</button>
            <button type="button" className="primary" onClick={() => { onAddColor(spectrumHex); onSelect(spectrumHex); }}>{lang === 'en' ? 'Add to tray' : 'הוסף למגש'}</button>
          </div>
        </div>
      )}

      {showMixer && (
        <div className="mixer">
          <div className="mixer-row">
            <label>
              <span>{lang === 'en' ? 'Color 1' : 'צבע 1'}</span>
              <input
                type="color"
                value={mixColor1}
                onChange={(e) => setMixColor1(e.target.value)}
              />
            </label>
            <label>
              <span>{lang === 'en' ? 'Ratio' : 'יחס'}</span>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={mixRatio}
                onChange={(e) => setMixRatio(Number(e.target.value))}
              />
            </label>
            <label>
              <span>{lang === 'en' ? 'Color 2' : 'צבע 2'}</span>
              <input
                type="color"
                value={mixColor2}
                onChange={(e) => setMixColor2(e.target.value)}
              />
            </label>
          </div>
          <div className="mixer-result">
            <div className="mixed-preview" style={{ background: mixed }} />
            <span className="mixed-hex">{mixed}</span>
            <button type="button" className="primary" onClick={handleAddMixed}>
              {lang === 'en' ? 'Add color to tray' : 'הוסף צבע למגש'}
            </button>
          </div>
        </div>
      )}

      <div className="current-color-display" style={{ background: currentColor }} title={currentColor} />
    </div>
  )
}
